import React, {useState, useEffect, useMemo} from "react";
import {useAuth} from "../../hooks/useAuth";
import {useSessionsByDM} from "../../hooks/useFirestore";
import {getSessionPlayerLogs, SessionPlayerLog} from "../../services/api/characterSheetService";
import FilterableList from "../../components/FilterableList";
import {BaseFilters} from "../../components/ListFilters";
import {getDisplayDate} from "../../utils/dateUtils";
import SessionListItem from "../../components/SessionListItem";
import "../../styles/env.css";

interface SessionLogFilters extends BaseFilters {
  status: "all" | "logged" | "not-logged";
}

interface SessionWithLogs {
  session: any;
  playerLogs: SessionPlayerLog[];
  loading: boolean;
  error?: string;
}

const SessionLogs: React.FC = () => {
  const {user} = useAuth();
  const {
    data: dmSessions,
    loading: sessionsLoading,
    error: sessionsError,
  } = useSessionsByDM(user?.uid || "");

  const [sessionLogs, setSessionLogs] = useState<SessionWithLogs[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // State for expanded sessions and player logs
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const [expandedPlayerLogs, setExpandedPlayerLogs] = useState<Record<string, boolean>>({});

  // Filters state
  const [filters, setFilters] = useState<SessionLogFilters>({
    search: "",
    status: "all",
  });

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: "",
      status: "all",
    });
  };

  // Toggle session expansion
  const toggleSessionExpansion = (sessionId: string) => {
    setExpandedSessionId(expandedSessionId === sessionId ? null : sessionId);
  };

  // Toggle player log expansion
  const togglePlayerLogExpansion = (logKey: string) => {
    setExpandedPlayerLogs(prev => ({
      ...prev,
      [logKey]: !prev[logKey]
    }));
  };

  // Fetch player logs for all sessions
  useEffect(() => {
    const fetchAllSessionLogs = async () => {
      if (!dmSessions || dmSessions.length === 0) {
        setSessionLogs([]);
        return;
      }

      setLoadingLogs(true);
      const logsPromises = dmSessions.map(async (session) => {
        try {
          const result = await getSessionPlayerLogs(session.id || "");
          return {
            session,
            playerLogs: result.playerLogs,
            loading: false,
          };
        } catch (error) {
          return {
            session,
            playerLogs: [],
            loading: false,
            error: error instanceof Error ? error.message : "Failed to load logs",
          };
        }
      });

      const results = await Promise.all(logsPromises);
      setSessionLogs(results);
      setLoadingLogs(false);
    };

    fetchAllSessionLogs();
  }, [dmSessions]);

  // Filter sessions based on criteria
  const filteredSessions = useMemo(() => {
    if (!sessionLogs || sessionLogs.length === 0) return [];

    let filtered = sessionLogs;

    // Filter by search term (session name, titan, or player names)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((sessionWithLogs) => {
        const session = sessionWithLogs.session;
        const sessionName = session.name?.toLowerCase() || "";
        const titanName = session.titanId?.toLowerCase() || "";
        
        // Check if any player name matches
        const playerMatch = sessionWithLogs.playerLogs.some(log => 
          log.playerName?.toLowerCase().includes(searchLower) ||
          log.characterName?.toLowerCase().includes(searchLower)
        );

        return sessionName.includes(searchLower) || 
               titanName.includes(searchLower) || 
               playerMatch;
      });
    }

    // Filter by log status
    if (filters.status !== "all") {
      filtered = filtered.filter((sessionWithLogs) => {
        if (filters.status === "logged") {
          return sessionWithLogs.playerLogs.some(log => log.hasLogged);
        } else if (filters.status === "not-logged") {
          return sessionWithLogs.playerLogs.some(log => !log.hasLogged);
        }
        return true;
      });
    }

    // Sort by session date (newest first)
    return filtered.sort((a, b) => {
      const dateA = a.session.date?.toDate ? a.session.date.toDate() : new Date(a.session.date);
      const dateB = b.session.date?.toDate ? b.session.date.toDate() : new Date(b.session.date);
      return dateB.getTime() - dateA.getTime();
    });
  }, [sessionLogs, filters]);

  // Render individual session log item
  const renderSessionLogItem = (sessionWithLogs: SessionWithLogs) => {
    const {session, playerLogs, loading, error} = sessionWithLogs;
    
    if (loading) {
      return (
        <div className="env-card" style={{padding: "16px"}}>
          <div>Loading session logs...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="env-card" style={{padding: "16px", borderColor: "#ff6b6b"}}>
          <div style={{color: "#ff6b6b"}}>Error loading logs: {error}</div>
        </div>
      );
    }

    const sessionDate = session.date?.toDate ? session.date.toDate() : new Date(session.date);
    const loggedCount = playerLogs.filter(log => log.hasLogged).length;
    const totalPlayers = playerLogs.length;
    const sessionId = session.id || "";
    const isExpanded = expandedSessionId === sessionId;

    // Convert player logs to Player format for SessionListItem
    const players = playerLogs.map(log => ({
      playerId: log.playerId,
      displayName: log.playerName,
      characterId: log.characterId,
      characterName: log.characterName,
      characterClass: log.characterClass,
      characterLevel: log.characterLevel,
    }));

    return (
      <div className="env-card" style={{marginBottom: "16px"}}>
        <SessionListItem
          id={sessionId}
          title={session.name}
          date={getDisplayDate(sessionDate)}
          location={session.location}
          maxPlayers={totalPlayers}
          players={players}
          isExpanded={isExpanded}
          onToggleExpand={() => toggleSessionExpansion(sessionId)}
          actionLabel={`${loggedCount}/${totalPlayers} Logged`}
          actionDisabled={true}
        />

        {/* Expanded content - Player logs */}
        {isExpanded && (
          <div style={{borderTop: "1px solid #eee", paddingTop: "16px", marginTop: "16px"}}>
            <h4 style={{margin: "0 0 12px 0", fontSize: "1rem"}}>Player Logs</h4>
            {playerLogs.length === 0 ? (
              <div style={{color: "#666", fontStyle: "italic"}}>No players in this session</div>
            ) : (
              <div style={{display: "flex", flexDirection: "column", gap: "12px"}}>
                {playerLogs.map((log) => {
                  const logKey = `${sessionId}-${log.playerId}-${log.characterId}`;
                  const isLogExpanded = expandedPlayerLogs[logKey];
                  
                  return (
                    <div 
                      key={logKey}
                      className={`session-list-item ${isLogExpanded ? "session-list-item-expanded" : ""}`}
                      style={{
                        backgroundColor: log.hasLogged ? "#f8f9fa" : "#fff3cd",
                        border: `1px solid ${log.hasLogged ? "#d1ecf1" : "#ffeaa7"}`,
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                      onClick={() => togglePlayerLogExpansion(logKey)}
                    >
                      <div className="session-list-item-content">
                        <button
                          className="session-list-item-toggle"
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePlayerLogExpansion(logKey);
                          }}
                          aria-label={isLogExpanded ? "Collapse" : "Expand"}
                        >
                          {isLogExpanded ? "▾" : "▸"}
                        </button>

                        <div className="session-list-item-info">
                          <span className="session-list-item-title">{log.playerName}</span>
                          <span className="session-list-item-separator">•</span>
                          <span className="session-list-item-dm">{log.characterName}</span>
                          <span className="session-list-item-separator">•</span>
                          <span className="session-list-item-datetime">
                            {log.characterClass} - Level {log.characterLevel}
                          </span>
                        </div>

                        <div className="session-list-item-actions">
                          <span style={{
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "0.8rem",
                            backgroundColor: log.hasLogged ? "#d4edda" : "#f8d7da",
                            color: log.hasLogged ? "#155724" : "#721c24",
                          }}>
                            {log.hasLogged ? "Logged" : "Not Logged"}
                          </span>
                        </div>
                      </div>

                      {/* Expanded log details */}
                      {isLogExpanded && log.hasLogged && log.logData && (
                        <div style={{padding: "16px", borderTop: "1px solid #eee", marginTop: "8px"}}>
                          <div style={{marginBottom: "8px"}}>
                            <strong>Events:</strong>
                            <div style={{marginTop: "4px", padding: "8px", backgroundColor: "#fff", borderRadius: "4px", fontSize: "0.9rem"}}>
                              {log.logData.events || "No events recorded"}
                            </div>
                          </div>
                          
                          {log.logData.currency && (log.logData.currency.gold > 0 || log.logData.currency.platinum > 0 || log.logData.currency.electrum > 0 || log.logData.currency.silver > 0 || log.logData.currency.copper > 0) && (
                            <div style={{marginBottom: "8px"}}>
                              <strong>Currency Earned:</strong>
                              <div style={{marginTop: "4px", fontSize: "0.9rem"}}>
                                {log.logData.currency.platinum > 0 && <div>• {log.logData.currency.platinum} Platinum</div>}
                                {log.logData.currency.gold > 0 && <div>• {log.logData.currency.gold} Gold</div>}
                                {log.logData.currency.electrum > 0 && <div>• {log.logData.currency.electrum} Electrum</div>}
                                {log.logData.currency.silver > 0 && <div>• {log.logData.currency.silver} Silver</div>}
                                {log.logData.currency.copper > 0 && <div>• {log.logData.currency.copper} Copper</div>}
                              </div>
                            </div>
                          )}
                          
                          {log.logData.items && log.logData.items.length > 0 && (
                            <div style={{marginBottom: "8px"}}>
                              <strong>Items:</strong>
                              <div style={{marginTop: "4px"}}>
                                {log.logData.items.map((item, itemIndex) => (
                                  <div key={itemIndex} style={{fontSize: "0.9rem", marginBottom: "2px"}}>
                                    • {item.name} (x{item.quantity}) {item.attunement ? "(Attuned)" : ""}
                                    {item.description && <div style={{color: "#666", fontSize: "0.8rem", marginLeft: "12px"}}>{item.description}</div>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          <div style={{fontSize: "0.8rem", color: "#666"}}>
                            Logged: {log.logData.loggedAt ? 
                              (log.logData.loggedAt.toDate ? 
                                log.logData.loggedAt.toDate().toLocaleString() : 
                                new Date(log.logData.loggedAt).toLocaleString()
                              ) : 
                              "Unknown"
                            }
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (sessionsLoading || loadingLogs) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Session Logs</h2>
        <div style={{padding: "20px", textAlign: "center"}}>
          Loading session logs...
        </div>
      </div>
    );
  }

  if (sessionsError) {
    return (
      <div className="env-page-container">
        <h2 className="env-page-title">Session Logs</h2>
        <div style={{padding: "20px", color: "#ff6b6b", textAlign: "center"}}>
          Error loading sessions: {sessionsError}
        </div>
      </div>
    );
  }

  return (
    <div className="env-page-container">
      <h2 className="env-page-title">Session Logs</h2>
      <section className="env-section" style={{height: "calc(100vh - 200px)"}}>
        <FilterableList
          data={filteredSessions}
          loading={false}
          error={null}
          filters={filters}
          onFiltersChange={setFilters}
          onClearFilters={clearFilters}
          searchFields={[]}
          searchPlaceholder="Search sessions by name, titan, or player..."
          emptyMessage="No session logs found matching your criteria"
          additionalFilter={() => true}
          renderItem={renderSessionLogItem}
          customActiveFilters={
            <>
              {filters.status !== "all" && (
                <span
                  style={{
                    backgroundColor: "var(--sub)",
                    padding: "4px 8px",
                    borderRadius: 4,
                    fontSize: "0.75rem",
                  }}
                >
                  Status: {filters.status === "logged" ? "Has Logs" : "Missing Logs"}
                </span>
              )}
            </>
          }
        >
          {/* Advanced filters */}
          <div
            style={{
              display: "flex",
              gap: 16,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <div style={{display: "flex", alignItems: "center", gap: 8}}>
              <label htmlFor="status-filter" style={{fontSize: "0.9rem"}}>
                Log Status:
              </label>
              <select
                id="status-filter"
                value={filters.status}
                onChange={(e) =>
                  setFilters({...filters, status: e.target.value as any})
                }
                style={{
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: "1px solid #ddd",
                  fontSize: "0.9rem",
                }}
              >
                <option value="all">All Sessions</option>
                <option value="logged">Has Logs</option>
                <option value="not-logged">Missing Logs</option>
              </select>
            </div>
          </div>
        </FilterableList>
      </section>
    </div>
  );
};

export default SessionLogs;
